import { supabase } from '../lib/supabase';
import { loadStripe } from '@stripe/stripe-js';

// Lazy initialize Stripe to avoid HTTPS warning on pages that only need helpers.
let stripePromise = null;
const getStripePromise = () => {
  if (stripePromise) return stripePromise;
  if (typeof window !== 'undefined' && window.location?.protocol !== 'https:') {
    return null;
  }
  stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
};

// Helper function to convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

// Helper function to convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const stripeService = {
  // Get Stripe instance
  getStripe: () => getStripePromise(),

  // Create subscription checkout session
  async createSubscriptionCheckout(userId, priceId, successUrl, cancelUrl) {
    try {
      // If priceId is not set or is a placeholder, return error with helpful message
      if (!priceId || priceId === 'price_premium_monthly' || priceId.startsWith('price_') === false) {
        return {
          data: null,
          error: {
            message: 'Stripe Price ID not configured. Please set VITE_STRIPE_PRICE_ID in your environment variables. For now, using payment intent as fallback.',
            code: 'PRICE_ID_NOT_SET'
          }
        };
      }

      // Get current session and access token
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError || !session || !session.access_token) {
        return {
          data: null,
          error: { message: 'Please log in to continue with subscription.' }
        };
      }

      // Explicitly pass Authorization header to ensure JWT is sent
      const { data, error } = await supabase?.functions?.invoke('create-subscription-checkout', {
        body: {
          userId,
          priceId,
          successUrl,
          cancelUrl
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env?.VITE_SUPABASE_ANON_KEY || ''
        }
      });

      if (error) {
        const userFriendlyError = {
          ...error,
          message: error?.message || 'Failed to create subscription checkout. Please try again.'
        };
        return { data: null, error: userFriendlyError };
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Create subscription checkout error:', error);
      return { 
        data: null, 
        error: { 
          message: 'Network error. Please check your connection and try again.',
          ...error 
        } 
      };
    }
  },

  // Create payment intent for one-time payment
  // Supports both formats:
  // 1. createPaymentIntent(amount, currency, metadata)
  // 2. createPaymentIntent({ amount, currency, metadata, userId, userEmail, ... })
  async createPaymentIntent(...args) {
    try {
      let amount, currency = 'INR', metadata = {};
      
      // Check if first argument is an object (new format)
      if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
        const params = args[0];
        amount = params.amount;
        currency = params.currency || 'INR';
        metadata = {
          userId: params.userId,
          userEmail: params.userEmail,
          subscriptionType: params.subscriptionType,
          ...params.metadata
        };
      } else {
        // Old format: (amount, currency, metadata)
        amount = args[0];
        currency = args[1] || 'INR';
        metadata = args[2] || {};
      }

      if (!amount || amount <= 0) {
        return {
          data: null,
          error: { message: 'Invalid amount. Amount must be greater than 0.' }
        };
      }

      // Refresh session so we have a valid JWT (avoids 401 Invalid JWT on edge functions)
      const { data: { session }, error: sessionError } = await supabase?.auth?.refreshSession();
      const accessToken = session?.access_token;

      if (sessionError || !session || !accessToken) {
        return {
          data: null,
          error: {
            message: 'Your session may have expired. Please log out, log in again, and try again.',
            code: 'SESSION_EXPIRED'
          }
        };
      }

      // Explicitly pass Authorization header - Supabase's auto-include sometimes fails
      const { data, error } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          amount: Math.round(amount * 100), // Convert to cents
          currency: currency?.toLowerCase() || 'inr',
          metadata
        },
        headers: {
          Authorization: `Bearer ${accessToken}`,
          apikey: import.meta.env?.VITE_SUPABASE_ANON_KEY || ''
        }
      });

      if (error) {
        // Try to get more details from the error
        let errorMessage = error?.message || 'Failed to create payment. Please try again.';
        let errorDetails = null;
        const status = error?.context?.status;

        // 401 / Invalid JWT → session expired message
        if (status === 401 || /invalid jwt|unauthorized|session/i.test(String(errorMessage))) {
          errorMessage = 'Your session may have expired. Please log out, log in again, and try again.';
          return { data: null, error: { ...error, message: errorMessage } };
        }

        // If it's a FunctionsHttpError, try to get the response body
        if (error?.context && error?.context instanceof Response) {
          try {
            const clonedResponse = error.context.clone();
            const errorBody = await clonedResponse.json();

            console.error('Edge Function error response:', errorBody);

            if (errorBody?.error) {
              errorMessage = errorBody.error;
              if (errorBody?.details) {
                errorDetails = errorBody.details;
                errorMessage += `: ${errorBody.details}`;
              }
              if (errorBody?.code) {
                errorMessage += ` (Code: ${errorBody.code})`;
              }
              if (status === 401 || /invalid jwt|unauthorized/i.test(String(errorBody?.error || ''))) {
                errorMessage = 'Your session may have expired. Please log out, log in again, and try again.';
              }
            }
          } catch (e) {
            console.error('Could not parse error response:', e);
            if (status === 401) {
              errorMessage = 'Your session may have expired. Please log out, log in again, and try again.';
            } else if (status === 500) {
              errorMessage = 'Server error. Please try again or contact support.';
            } else if (status === 400) {
              errorMessage = 'Invalid request. Please check your payment details.';
            }
          }
        }

        console.error('Payment intent creation error:', error);
        console.error('Error context:', error?.context);
        const userFriendlyError = {
          ...error,
          message: errorMessage,
          details: errorDetails
        };
        return { data: null, error: userFriendlyError };
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Create payment intent error:', error);
      return { 
        data: null, 
        error: { 
          message: 'Network error. Please check your connection and try again.',
          ...error 
        } 
      };
    }
  },

  // Get user's active subscription
  async getActiveSubscription(userId) {
    try {
      const { data, error } = await supabase
        ?.from('stripe_subscriptions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.in('status', ['active', 'trialing'])
        ?.order('created_at', { ascending: false })
        ?.limit(1);

      // Handle table not found (404), no rows found (PGRST116), or not acceptable (406)
      // 406 can occur when RLS blocks the query or when there are no matching rows
      if (error) {
        // Table doesn't exist, no subscription found, or RLS blocking - return null gracefully
        if (error?.code === 'PGRST116' || error?.code === '42P01' || error?.status === 404 || error?.status === 406) {
          return { data: null, error: null };
        }
        return { 
          data: null, 
          error: { 
            ...error,
            message: error?.message || 'Failed to load subscription. Please try again.'
          } 
        };
      }
      
      // If we got data, return the first item (or null if empty)
      if (data && data.length > 0) {
        return { data: toCamelCase(data[0]), error: null };
      }
      
      // No data found - this is a valid state (user has no active subscription)
      return { data: null, error: null };
    } catch (error) {
      console.error('Get active subscription error:', error);
      // If table doesn't exist, return null gracefully
      if (error?.code === '42P01' || error?.message?.includes('does not exist')) {
        return { data: null, error: null };
      }
      return { 
        data: null, 
        error: { 
          message: 'Network error. Please check your connection and try again.',
          ...error 
        } 
      };
    }
  },

  // Get user's subscription history
  async getSubscriptionHistory(userId) {
    try {
      const { data, error } = await supabase
        ?.from('stripe_subscriptions')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      // Handle table not found (404), not acceptable (406), or other errors gracefully
      if (error) {
        if (error?.code === '42P01' || error?.status === 404 || error?.status === 406 || error?.message?.includes('does not exist')) {
          return { data: [], error: null };
        }
        return { 
          data: [], 
          error: { 
            ...error,
            message: error?.message || 'Failed to load subscription history. Please try again.'
          } 
        };
      }
      return { data: data ? toCamelCase(data) : [], error: null };
    } catch (error) {
      console.error('Get subscription history error:', error);
      // If table doesn't exist, return empty array gracefully
      if (error?.code === '42P01' || error?.message?.includes('does not exist')) {
        return { data: [], error: null };
      }
      return { 
        data: [], 
        error: { 
          message: 'Network error. Please check your connection and try again.',
          ...error 
        } 
      };
    }
  },

  // Get user's payment history
  async getPaymentHistory(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        ?.from('stripe_payments')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      // Handle table not found (404), not acceptable (406), or other errors gracefully
      if (error) {
        if (error?.code === '42P01' || error?.status === 404 || error?.status === 406 || error?.message?.includes('does not exist')) {
          return { data: [], error: null };
        }
        console.error('Get payment history error:', error);
        return { data: [], error };
      }
      return { data: data ? toCamelCase(data) : [], error: null };
    } catch (error) {
      console.error('Get payment history error:', error);
      // If table doesn't exist, return empty array gracefully
      if (error?.code === '42P01' || error?.message?.includes('does not exist')) {
        return { data: [], error: null };
      }
      return { data: [], error };
    }
  },

  // Cancel subscription
  async cancelSubscription(subscriptionId, cancelAtPeriodEnd = true) {
    try {
      // Get current session and access token
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError || !session || !session.access_token) {
        return {
          data: null,
          error: { message: 'Please log in to manage your subscription.' }
        };
      }

      // Explicitly pass Authorization header to ensure JWT is sent
      const { data, error } = await supabase?.functions?.invoke('cancel-subscription', {
        body: {
          subscriptionId,
          cancelAtPeriodEnd
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env?.VITE_SUPABASE_ANON_KEY || ''
        }
      });

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Cancel subscription error:', error);
      return { data: null, error };
    }
  },

  // Resume subscription
  async resumeSubscription(subscriptionId) {
    try {
      // Get current session and access token
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError || !session || !session.access_token) {
        return {
          data: null,
          error: { message: 'Please log in to manage your subscription.' }
        };
      }

      // Explicitly pass Authorization header to ensure JWT is sent
      const { data, error } = await supabase?.functions?.invoke('resume-subscription', {
        body: { subscriptionId },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env?.VITE_SUPABASE_ANON_KEY || ''
        }
      });

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Resume subscription error:', error);
      return { data: null, error };
    }
  },

  // Get customer portal URL
  async getCustomerPortalUrl(returnUrl) {
    try {
      // Get current session and access token
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      
      if (sessionError || !session || !session.access_token) {
        return {
          data: null,
          error: { message: 'Please log in to access customer portal.' }
        };
      }

      // Explicitly pass Authorization header to ensure JWT is sent
      const { data, error } = await supabase?.functions?.invoke('create-customer-portal', {
        body: { returnUrl },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env?.VITE_SUPABASE_ANON_KEY || ''
        }
      });

      if (error) throw error;
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Get customer portal URL error:', error);
      return { data: null, error };
    }
  },

  // Format currency amount
  formatAmount(amount, currency = 'INR') {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  },

  // User-friendly payment status label (raw Stripe status -> display text)
  getPaymentStatusLabel(status) {
    const s = status?.toLowerCase();
    if (s === 'succeeded') return 'Paid';
    if (s === 'requires_payment_method' || s === 'requires_confirmation' || s === 'requires_action') return 'Pending';
    if (s === 'processing') return 'Processing';
    if (s === 'failed' || s === 'canceled' || s === 'incomplete_expired') return 'Failed';
    if (s === 'incomplete') return 'Incomplete';
    if (s === 'past_due') return 'Past due';
    return status ? String(status).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—';
  },

  // Get payment status badge color
  getPaymentStatusColor(status) {
    switch (status?.toLowerCase()) {
      case 'succeeded': case 'active': case 'trialing':
        return 'text-success bg-success/10';
      case 'pending': case 'processing': case 'requires_payment_method': case 'requires_confirmation': case 'requires_action':
        return 'text-warning bg-warning/10';
      case 'failed': case 'canceled': case 'incomplete': case 'incomplete_expired':
        return 'text-error bg-error/10';
      case 'past_due':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  },

  // Get payment status icon
  getPaymentStatusIcon(status) {
    switch (status?.toLowerCase()) {
      case 'succeeded': case 'active': case 'trialing':
        return 'CheckCircle';
      case 'pending': case 'processing': case 'requires_payment_method': case 'requires_confirmation': case 'requires_action':
        return 'Clock';
      case 'failed': case 'canceled': case 'incomplete': case 'incomplete_expired':
        return 'XCircle';
      case 'past_due':
        return 'AlertCircle';
      default:
        return 'Circle';
    }
  }
};

export default stripeService;